import { Box, Button, TextField, MenuItem, Typography, CircularProgress, InputAdornment, alpha, Dialog, DialogTitle, DialogContent, DialogActions, Alert, IconButton } from "@mui/material";
import { useEffect, useState, useCallback, useRef } from "react";
import FieldVisitTable from "page-sections/FieldVisit/FieldVisitTable";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";
import { GetApp, Search, CloudUpload, Close } from "@mui/icons-material";
import FieldVisitExportModal from "page-sections/FieldVisit/FieldVisitExportModal";
import { useToast } from "OtherReusableFunctionality/ToastContext";
// ─── CHANGE 1: Import live permission helper ───────────────────────────────────
import { getPermissionValue } from "config/ConfigAccessButton";
// ─────────────────────────────────────────────────────────────────────────────

const FieldVisit = () => {
    const { setPageTitle } = usePageTitle();
    const toast = useToast();
    const [fieldVisits, setFieldVisits] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [companyFilter, setCompanyFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [totalVisits, setTotalVisits] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);

    // Field Upload states
    const [fieldUploadDialogOpen, setFieldUploadDialogOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const fileInputRef = useRef(null);

    const [pagination, setPagination] = useState({
        current_page: 1,
        page_size: 10,
        total_items: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false,
        next_page_number: null,
        previous_page_number: null,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = [10, 25, 50, 100];
    const [viewMode, setViewMode] = useState("list");
    const [calendarVisits, setCalendarVisits] = useState([]);
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [permissionTick, setPermissionTick] = useState(0);

    useEffect(() => {
        setPageTitle('Field Visit');
    }, [setPageTitle]);

    useEffect(() => {
        const onPermissionsUpdated = () => setPermissionTick((v) => v + 1);
        window.addEventListener("permissions-updated", onPermissionsUpdated);
        return () => window.removeEventListener("permissions-updated", onPermissionsUpdated);
    }, []);

    // Live permission check (same pattern as Export to Excel / View Image)
    const can = (action) => {
        void permissionTick;
        return getPermissionValue("fieldVisit", action) === true;
    };

    useEffect(() => {
        if (!can("calendar") && viewMode === "calendar") {
            setViewMode("list");
        }
    }, [permissionTick, viewMode]);

    const isCompanyUser = apiService.isCompanyUser();
    const companyId = apiService.getCompanyId();

    const fetchCompanies = useCallback(async () => {
        if (isCompanyUser) {
            const details = apiService.getCompanyDetails();
            const id = apiService.getCompanyId();
            const singleCompany = {
                id: id,
                name: details?.name || "My Company",
            };
            setCompanyOptions([singleCompany]);
            setCompanyFilter(id?.toString() || "");
            return;
        }

        setLoadingCompanies(true);
        try {
            const response = await apiService.get(API_ENDPOINTS.GET_company_choices);
            const options = response?.data?.Company || response?.data?.choices || response?.data || [];
            if (Array.isArray(options)) {
                setCompanyOptions(options);
            } else {
                setCompanyOptions([]);
            }
        } catch (error) {
            console.error("Error fetching companies:", error);
            setCompanyOptions([]);
        } finally {
            setLoadingCompanies(false);
        }
    }, [isCompanyUser]);

    const fetchCategories = useCallback(async (selectedCompanyId = null) => {
        setLoadingCategories(true);
        try {
            const targetCompanyId = selectedCompanyId || (isCompanyUser ? companyId : null);
            
            if (!targetCompanyId) {
                setCategories([]);
                return;
            }

            const userId = localStorage.getItem('user_id');
            const response = await apiService.get(`${API_ENDPOINTS.GET_product_category}?company=${targetCompanyId}&user_id=${userId}`);
            
            if (response?.success) {
                const categoryList = response.data?.ProductCategory || [];
                setCategories(categoryList);
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            setCategories([]);
        } finally {
            setLoadingCategories(false);
        }
    }, [isCompanyUser, companyId]);

    const fetchFieldVisits = useCallback(async (selectedCompanyId = null, page = 1, pageSizeParam = 10, search = "") => {
        setLoading(true);
        try {
            const targetCompanyId = selectedCompanyId || (isCompanyUser ? companyId : null);
            const searchTrimmed = search && search.trim() ? search.trim() : "";

            // Load regular + assigned lists, then paginate the merged result client-side
            let url = API_ENDPOINTS.GET_field_visit;
            const params = new URLSearchParams();
            params.set("page", "1");
            params.set("page_size", "500");
            if (targetCompanyId) params.set("company_id", targetCompanyId);
            if (searchTrimmed) params.set("search", searchTrimmed);
            url += (url.includes("?") ? "&" : "?") + params.toString();

            const response = await apiService.get(url);
            const regularList =
                response?.success && Array.isArray(response?.data?.FieldVisit)
                    ? response.data.FieldVisit
                    : [];

            let assignedList = [];
            try {
                let assignedUrl = API_ENDPOINTS.GET_assigned_field_visit;
                const assignedParams = new URLSearchParams();
                assignedParams.set("page", "1");
                assignedParams.set("page_size", "500");
                if (searchTrimmed) assignedParams.set("search", searchTrimmed);
                assignedUrl += (assignedUrl.includes("?") ? "&" : "?") + assignedParams.toString();
                const assignedResponse = await apiService.get(assignedUrl);
                const assignedRaw =
                    assignedResponse?.data?.AssignedFieldVisit ||
                    assignedResponse?.data?.assigned_field_visit ||
                    assignedResponse?.data?.FieldVisit ||
                    (Array.isArray(assignedResponse?.data) ? assignedResponse.data : []);
                assignedList = Array.isArray(assignedRaw) ? assignedRaw : [];
            } catch (assignedError) {
                console.error("Assigned field visit fetch error:", assignedError);
                assignedList = [];
            }

            const regularIds = new Set(regularList.map((item) => String(item?.id)));
            const assignedOnly = assignedList
                .filter((item) => !regularIds.has(String(item?.id)))
                .map((item) => ({ ...item, __isAssigned: true }));
            const regularMapped = regularList.map((item) => ({ ...item, __isAssigned: false }));
            const merged = [...assignedOnly, ...regularMapped];

            const totalItems = merged.length;
            const totalPages = Math.max(1, Math.ceil(totalItems / pageSizeParam) || 1);
            const safePage = Math.min(Math.max(1, page), totalPages);
            const start = (safePage - 1) * pageSizeParam;
            setFieldVisits(merged.slice(start, start + pageSizeParam));
            setPagination({
                current_page: safePage,
                page_size: pageSizeParam,
                total_items: totalItems,
                total_pages: totalPages,
                has_next: safePage < totalPages,
                has_previous: safePage > 1,
                next_page_number: safePage < totalPages ? safePage + 1 : null,
                previous_page_number: safePage > 1 ? safePage - 1 : null,
            });
            setCurrentPage(safePage);
            setPageSize(pageSizeParam);
        } catch (error) {
            console.error("API error:", error);
            setFieldVisits([]);
            setPagination((prev) => ({
                ...prev,
                current_page: 1,
                page_size: pageSizeParam,
                total_items: 0,
                total_pages: 1,
                has_next: false,
                has_previous: false,
                next_page_number: null,
                previous_page_number: null,
            }));
        } finally {
            setLoading(false);
        }
    }, [isCompanyUser, companyId]);

    const handleFieldVisitPageChange = (_, value) => {
        setCurrentPage(value);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, value, pageSize, searchTerm);
    };

    const handleFieldVisitPageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setCurrentPage(1);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, 1, newSize, searchTerm);
    };

    const handleSearch = () => {
        if (!searchValue.trim()) return;
        setSearchTerm(searchValue);
        setCurrentPage(1);
        setHasSearched(true);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, 1, pageSize, searchValue);
    };

    const handleClearSearch = () => {
        setSearchValue("");
        setSearchTerm("");
        setCurrentPage(1);
        setHasSearched(false);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, 1, pageSize, "");
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && searchValue.trim()) {
            handleSearch();
        }
    };

    useEffect(() => {
        if (searchValue === "" && searchTerm !== "") {
            handleClearSearch();
        }
    }, [searchValue]);

    const fetchDashboardData = useCallback(async () => {
        setLoadingDashboard(true);
        try {
            const today = new Date();
            const dateFrom = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
            const dateTo = new Date(today.getFullYear() + 1, 0, 31).toISOString().split('T')[0];
            
            const url = `${API_ENDPOINTS.GET_field_visit_dashboard}?user_type=agent`;
            const response = await apiService.get(url);
            
            if (response?.success && response?.data?.summary) {
                setTotalVisits(response.data.summary.total_visits);
            } else {
                setTotalVisits(null);
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            setTotalVisits(null);
        } finally {
            setLoadingDashboard(false);
        }
    }, []);

    const fetchCalendarVisits = useCallback(async () => {
        setCalendarLoading(true);
        try {
            let url = API_ENDPOINTS.GET_assigned_field_visit;
            const params = new URLSearchParams();
            params.set("page", "1");
            params.set("page_size", "500");
            url += (url.includes("?") ? "&" : "?") + params.toString();

            const response = await apiService.get(url);
            const list =
                response?.data?.AssignedFieldVisit ||
                response?.data?.assigned_field_visit ||
                response?.data?.FieldVisit ||
                (Array.isArray(response?.data) ? response.data : []);

            setCalendarVisits(Array.isArray(list) ? list : []);
        } catch (error) {
            console.error("Calendar API error:", error);
            setCalendarVisits([]);
        } finally {
            setCalendarLoading(false);
        }
    }, []);

    const handleAssignFieldVisit = async (payload) => {
        try {
            await apiService.post(API_ENDPOINTS.POST_assign_field_visit, payload);
            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
            fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
            fetchCalendarVisits();
            fetchDashboardData();
        } catch (error) {
            console.error("Error assigning field visit:", error);
            throw error;
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    useEffect(() => {
        if (companyFilter || (isCompanyUser && companyId)) {
            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
            fetchCategories(targetCompanyId);
            setCategoryFilter("");
        } else {
            setCategories([]);
            setCategoryFilter("");
        }
    }, [companyFilter, isCompanyUser, companyId, fetchCategories]);

    useEffect(() => {
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
    }, [companyFilter, isCompanyUser, companyId, fetchFieldVisits]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    useEffect(() => {
        fetchCalendarVisits();
    }, [fetchCalendarVisits]);

    const handleCompanyFilterChange = (e) => {
        setCompanyFilter(e.target.value);
        setSearchTerm("");
        setSearchValue("");
        setHasSearched(false);
    };

    const handleCategoryFilterChange = (e) => {
        setCategoryFilter(e.target.value);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        if (targetCompanyId) {
            fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
        }
    };

    const handleExportClick = () => {
        setExportModalOpen(true);
    };

    const handleExportModalClose = () => {
        setExportModalOpen(false);
    };

    // Field Upload Handlers
    const openFieldUploadDialog = () => {
        setFieldUploadDialogOpen(true);
        setUploadFile(null);
        setUploadError("");
        setUploadSuccess(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const closeFieldUploadDialog = () => {
        setFieldUploadDialogOpen(false);
        setUploadFile(null);
        setUploadError("");
        setUploadSuccess(false);
        setUploading(false);
        setDownloadingTemplate(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Download Template — uses apiService so Authorization + token refresh work
    const handleDownloadTemplate = async () => {
        setDownloadingTemplate(true);
        setUploadError("");

        try {
            if (!apiService.getAccessToken()) {
                throw new Error("No authentication token found. Please login again.");
            }

            await apiService.downloadFromExportEndpoint(API_ENDPOINTS.download_field_visit_template, {
                method: 'get',
                fallbackFilename: 'field_visit_template.xlsx',
            });

            toast.showSuccess("Template downloaded successfully");
        } catch (error) {
            console.error("Template download failed:", error);
            const errorMessage = error?.response?.data?.detail || error?.message || "Failed to download template. Please try again.";
            setUploadError(typeof errorMessage === 'string' ? errorMessage : "Failed to download template. Please try again.");
            toast.showError(typeof errorMessage === 'string' ? errorMessage : "Failed to download template. Please try again.");
        } finally {
            setDownloadingTemplate(false);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            const validExtensions = ['.xlsx', '.xls', '.xlsm', '.csv'];
            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

            if (validExtensions.includes(fileExtension)) {
                setUploadFile(file);
                setUploadError("");
                setUploadSuccess(false);
            } else {
                setUploadError("Please select a valid Excel file (.xlsx, .xls, .xlsm, or .csv)");
                event.target.value = null;
            }
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file && (file.type.includes('sheet') || file.name.match(/\.(xlsx|xls|xlsm|csv)$/))) {
            setUploadFile(file);
            setUploadError("");
            setUploadSuccess(false);
        } else {
            setUploadError("Please select a valid Excel file (.xlsx, .xls, .xlsm, or .csv)");
        }
    };

    const handleFieldUpload = async () => {
        if (!uploadFile) {
            setUploadError("Please select a file to upload");
            return;
        }

        setUploading(true);
        setUploadError("");
        setUploadSuccess(false);

        try {
            const formData = new FormData();
            formData.append("excel_file", uploadFile);

            const userId = localStorage.getItem('user_id');
            if (userId) {
                formData.append("added_by", userId);
            }

            // apiService interceptor attaches the current accessToken (and refreshes on 401)
            const response = await apiService.post(API_ENDPOINTS.POST_field_visit_excel, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response?.success) {
                setUploadSuccess(true);
                const message = response?.message || "Field visits uploaded successfully";
                if (/no new (report|records) imported/i.test(message)) {
                    toast.showError(message);
                } else {
                    toast.showSuccess(message);
                }
                setTimeout(() => {
                    closeFieldUploadDialog();
                    const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                    fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
                    fetchDashboardData();
                    fetchCalendarVisits();
                }, 2000);
            } else {
                setUploadError(response?.message || "Upload failed. Please try again.");
                toast.showError(response?.message || "Upload failed");
            }
        } catch (error) {
            console.error("Field Upload failed:", error);
            const errorMessage = error?.response?.data?.message || error?.message || "Upload failed. Please check your file and try again.";
            setUploadError(errorMessage);
            toast.showError(errorMessage);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box pt={2} pb={4}>
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: (theme) => theme.zIndex.appBar - 1,
                    backgroundColor: "background.default",
                    pt: 0.5,
                    pb: 1,
                    mb: 1,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: "12px",
                        p: "12px 16px",
                        mb: 2,
                        bgcolor: "background.paper",
                        borderRadius: "10px",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                        border: "1px solid #e5e7eb",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            flex: { xs: "1 1 100%", lg: "1 1 0" },
                            minWidth: { xs: "100%", lg: 280 },
                        }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by Date, farmer name, Contact, village etc"
                            value={searchValue}
                            onChange={(e) => {
                                setSearchValue(e.target.value);
                                if (hasSearched && e.target.value !== searchTerm) {
                                    setHasSearched(false);
                                }
                            }}
                            onKeyPress={handleKeyPress}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ fontSize: 20, color: "text.disabled" }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                "& .MuiOutlinedInput-root": {
                                    height: 40,
                                    borderRadius: "8px",
                                    fontSize: 14,
                                    pl: 0.5,
                                    "& fieldset": { borderColor: "#d1d5db" },
                                    "&:hover fieldset": { borderColor: "#9ca3af" },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#d1d5db",
                                        boxShadow: "none",
                                    },
                                },
                            }}
                        />

                        <Button
                            variant="contained"
                            startIcon={<Search />}
                            onClick={handleSearch}
                            disabled={!searchValue.trim() || (searchValue === searchTerm && hasSearched)}
                            sx={{
                                height: 40,
                                minWidth: 90,
                                borderRadius: 2,
                                fontSize: 14,
                                fontWeight: 500,
                                textTransform: "none",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                px: 2,
                                boxShadow: "none",
                                "&:hover": { boxShadow: "none" },
                            }}
                        >
                            Search
                        </Button>
                    </Box>

                    {!isCompanyUser && (
                        <TextField
                            select
                            size="small"
                            value={companyFilter}
                            onChange={handleCompanyFilterChange}
                            disabled={loading || loadingCompanies}
                            SelectProps={{
                                displayEmpty: true,
                                renderValue: (selected) => {
                                    if (!selected) {
                                        return (
                                            <Typography component="span" sx={{ color: "text.secondary", fontSize: 14 }}>
                                                All Category
                                            </Typography>
                                        );
                                    }
                                    const company = companyOptions.find(
                                        (item) => String(item.id) === String(selected)
                                    );
                                    return company?.name || "All Category";
                                },
                            }}
                            sx={{
                                width: 160,
                                flexShrink: 0,
                                "& .MuiOutlinedInput-root": {
                                    height: 40,
                                    borderRadius: "8px",
                                    fontSize: 14,
                                    "& fieldset": { borderColor: "#d1d5db" },
                                },
                            }}
                        >
                            <MenuItem value="">All Category</MenuItem>
                            {loadingCompanies ? (
                                <MenuItem disabled>
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                    Loading companies...
                                </MenuItem>
                            ) : (
                                companyOptions.map((company) => (
                                    <MenuItem key={company.id} value={company.id}>
                                        {company.name}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    )}

                    {totalVisits !== null && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 40,
                                px: "14px",
                                py: "6px",
                                borderRadius: "20px",
                                bgcolor: "#f0fdf4",
                                border: "1px solid #bbf7d0",
                                color: "#166534",
                                fontSize: 13,
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            {loadingDashboard ? (
                                <CircularProgress size={16} sx={{ color: "#166534" }} />
                            ) : (
                                `Total Field Visits: ${totalVisits.toLocaleString()}`
                            )}
                        </Box>
                    )}

                    <Button
                        variant="contained"
                        startIcon={<CloudUpload />}
                        onClick={() => can('field_upload') && openFieldUploadDialog()}
                        disabled={!can('field_upload')}
                        sx={{
                            height: 40,
                            borderRadius: 2,
                            px: 2,
                            fontSize: 14,
                            fontWeight: 500,
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            boxShadow: "none",
                            opacity: !can('field_upload') ? 0.6 : 1,
                            "&:hover": { boxShadow: "none" },
                        }}
                    >
                        Bulk Field Visit Upload
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<GetApp />}
                        onClick={() => can('export_to_excel') && handleExportClick()}
                        disabled={!can('export_to_excel')}
                        sx={{
                            height: 40,
                            borderRadius: 2,
                            px: 2,
                            fontSize: 14,
                            fontWeight: 500,
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            opacity: !can('export_to_excel') ? 0.6 : 1,
                            boxShadow: "none",
                            "&:hover": { boxShadow: "none" },
                        }}
                    >
                        Export to Excel
                    </Button>
                </Box>
            </Box>

            <Box sx={{ mt: 1 }}>
                <FieldVisitTable
                    fieldVisits={fieldVisits}
                    searchQuery={searchTerm}
                    loading={loading}
                    onRefresh={() => {
                        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                        fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
                        fetchDashboardData();
                        fetchCalendarVisits();
                    }}
                    pagination={pagination}
                    onPageChange={handleFieldVisitPageChange}
                    onPageSizeChange={handleFieldVisitPageSizeChange}
                    pageSizeOptions={pageSizeOptions}
                    // ─── CHANGE 4: Pass view_image permission to table ─────────────────────
                    canViewImage={can('view_image')}
                    canAssign={can('assign_field_visit')}
                    canUseCalendar={can('calendar')}
                    // ───────────────────────────────────────────────────────────────────────
                    onAssign={handleAssignFieldVisit}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    calendarVisits={calendarVisits}
                    calendarLoading={calendarLoading}
                />
            </Box>

            <FieldVisitExportModal
                open={exportModalOpen}
                onClose={handleExportModalClose}
                onRefresh={() => {
                    const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                    fetchFieldVisits(targetCompanyId, currentPage, pageSize, searchTerm);
                }}
            />

            {/* Field Upload Dialog */}
            <Dialog open={fieldUploadDialogOpen} onClose={closeFieldUploadDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CloudUpload color="primary" />
                            <Typography variant="h6">Field Upload</Typography>
                        </Box>
                        <IconButton onClick={closeFieldUploadDialog} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: (theme) => theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.05)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: (theme) => theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.2)'
                            : 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px',
                        '&:hover': {
                            background: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.3)'
                                : 'rgba(0, 0, 0, 0.3)',
                        },
                    },
                    scrollbarWidth: 'thin',
                }}>
                    <Box sx={{ mt: 2 }}>
                        {/* Step 1: Format Check */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.1), borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Step 1: Check Your Format
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                If you already have data in the correct format with all required columns, you can skip to Step 3.
                                Otherwise, download the template to ensure proper formatting.
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                Required columns: Visit Date, Farmer Name, Village, Contact, Product Vertical, Crop, Products, Farm Area, etc.
                            </Typography>
                        </Box>

                        {/* Download Template Section */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: (theme) => alpha(theme.palette.info.main, 0.1), borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Step 2: Download Template (Optional)
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                Download the Excel template to ensure your data is in the correct format.
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={downloadingTemplate ? <CircularProgress size={16} /> : <GetApp />}
                                onClick={handleDownloadTemplate}
                                disabled={downloadingTemplate}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                {downloadingTemplate ? (
                                    <>
                                        <CircularProgress size={16} sx={{ mr: 1 }} />
                                        Downloading...
                                    </>
                                ) : (
                                    'Download Template'
                                )}
                            </Button>
                        </Box>

                        {/* Upload Section */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Step 3: Upload Your File
                            </Typography>

                            <Box
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                sx={(theme) => ({
                                    border: `2px dashed ${uploadFile ? theme.palette.primary.main : theme.palette.divider}`,
                                    borderRadius: theme.shape.borderRadius * 2,
                                    padding: theme.spacing(4),
                                    textAlign: 'center',
                                    backgroundColor: uploadFile ? alpha(theme.palette.primary.main, 0.1) : theme.palette.background.paper,
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    mb: 2,
                                    '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                    }
                                })}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls,.xlsm,.csv"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />

                                <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />

                                {uploadFile ? (
                                    <Box>
                                        <Typography variant="h6" color="primary">
                                            {uploadFile.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Typography variant="h6" sx={{ mb: 1 }}>
                                            Click to select Excel file or drag & drop
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Supports .xlsx, .xls, .xlsm, and .csv files
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Error Message */}
                        {uploadError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {uploadError}
                            </Alert>
                        )}

                        {/* Success Message */}
                        {uploadSuccess && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                File uploaded successfully! Field visits are being processed.
                            </Alert>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={closeFieldUploadDialog}
                        disabled={uploading}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleFieldUpload}
                        variant="contained"
                        disabled={!uploadFile || uploading}
                        startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FieldVisit;